import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  applyTypography,
  BaseButton,
  getCurrentPathWithQuery,
  saveAuthReturnTo,
  LangSelector,
  useAuth,
  useT,
} from '@repo/common';
import iconLogin24 from '@repo/common/assets/icons/24px/icon_24px_login.svg';
import iconBag32 from '@repo/common/assets/icons/32px/icon_32px_shopping-bag.svg';
import iconHuman28 from '@repo/common/assets/icons/28px/icon_28px_human.svg';
import { ReactComponent as ProfileIcon } from '@repo/common/assets/icons/16px/icon_16px_profile_outline.svg';
import { ReactComponent as OfficeWorkerIcon } from '@repo/common/assets/icons/16px/icon_16px_office-worker_outline.svg';
import { ReactComponent as BackpackIcon } from '@repo/common/assets/icons/16px/icon_16px_backpack_outline.svg';
import { ReactComponent as SettingIcon } from '@repo/common/assets/icons/16px/icon_16px_setting_outline.svg';
import { logoutUser } from '../../api/user/user.api';
import { getCartItems } from '../../api/cart/cart.api';
import { CART_CHANGED_EVENT } from '../../utils/cartStatus';

const cx = (...classes) => classes.filter(Boolean).join(' ');
const BUSINESS_ROLES = new Set([
  'E',
  'ROLE_E',
  'EMPLOYER',
  'ROLE_EMPLOYER',
  'EMPLOYEE',
  'ROLE_EMPLOYEE',
]);

const ACCOUNT_CONVERSION_RETURN_TO = '/mypage?tab=Account%20setting';

const DROPDOWN_ITEM_CLASS =
  'cursor-pointer rounded-lg border-0 bg-transparent p-1 text-left transition-colors duration-sm ease hover:bg-grayscale-gray02 focus-visible:bg-grayscale-gray02 focus-visible:outline-none motion-reduce:transition-none';

function RightControls({ mode = 'default' }) {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, loading } = useAuth();
  const isBusiness = BUSINESS_ROLES.has(
    String(user?.role ?? '').toUpperCase(),
  );
  // 서버 응답이 creator / isCreator 두 형태로 올 수 있어 둘 다 확인 (AccountSetting 과 동일)
  const isCreator = Boolean(user?.creator ?? user?.isCreator);
  const modeAttr = mode === 'scrolled' ? 'scrolled' : undefined;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasCartItems, setHasCartItems] = useState(false);
  const dropdownRef = useRef(null);
  const profileButtonRef = useRef(null);
  const loginSectionPaddingClass =
    mode === 'scrolled'
      ? 'tabletH:pr-[12px] tabletV:pr-[12px] desktopL:pr-[14px] desktopXXL:pr-[24px]'
      : 'tabletH:pr-[12px] tabletV:pr-[14px] desktopXXL:pr-[18px]';

  const profileUrl = user?.profileUrl;
  const profileImage =
    profileUrl &&
    !profileUrl.startsWith('data:image/') &&
    !profileUrl.startsWith('http://') &&
    !profileUrl.startsWith('https://')
      ? `data:image/jpeg;base64,${profileUrl}`
      : profileUrl || null;

  const userId = user?.id ?? null;

  const checkCartStatus = useCallback(async () => {
    if (!userId) {
      setHasCartItems(false);
      return;
    }

    try {
      const cartItems = await getCartItems({ skipLoading: true });
      setHasCartItems(Boolean(cartItems?.length));
    } catch (error) {
      console.error('Failed to check cart status:', error);
      setHasCartItems(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isBusiness) {
      setHasCartItems(false);
      return undefined;
    }

    checkCartStatus();

    const interval = setInterval(checkCartStatus, 30000);
    window.addEventListener(CART_CHANGED_EVENT, checkCartStatus);
    return () => {
      clearInterval(interval);
      window.removeEventListener(CART_CHANGED_EVENT, checkCartStatus);
    };
  }, [checkCartStatus, isBusiness]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  const handleLoginClick = () => {
    const returnTo =
      `${location.pathname}${location.search}${location.hash}` ||
      getCurrentPathWithQuery();
    saveAuthReturnTo(returnTo);
    navigate('/login', { state: { returnTo } });
  };

  const handleProfileClick = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleLogoutClick = async () => {
    const currentPath =
      `${location.pathname}${location.search}${location.hash}` ||
      getCurrentPathWithQuery();

    try {
      await logoutUser();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setHasCartItems(false);
      setIsDropdownOpen(false);
      localStorage.removeItem('rememberMe');
      window.location.assign(currentPath);
    }
  };

  const handleMyProfileClick = () => {
    closeDropdown();
    navigate('/mypage?tab=My Profile');
  };

  const handleForBusinessClick = () => {
    closeDropdown();
    navigate('/register/additional-info', {
      state: {
        entry: 'settings',
        role: 'E',
        isCreator: false,
        returnTo: ACCOUNT_CONVERSION_RETURN_TO,
      },
    });
  };

  const handleBecomeCreatorClick = () => {
    closeDropdown();
    navigate('/register/additional-info', {
      state: {
        entry: 'settings',
        role: 'U',
        isCreator: true,
        returnTo: ACCOUNT_CONVERSION_RETURN_TO,
      },
    });
  };

  const handleMyApplyClick = () => {
    closeDropdown();
    navigate('/mypage?tab=My Apply');
  };

  const handleActivitiesClick = () => {
    closeDropdown();
    navigate('/mypage?tab=Activities');
  };

  const handleSettingsClick = () => {
    closeDropdown();
    navigate('/mypage?tab=Account setting');
  };

  const profileMenuItems = isBusiness
    ? [
        {
          label: 'Profile',
          Icon: ProfileIcon,
          onClick: handleMyProfileClick,
        },
        {
          label: 'Jobs',
          Icon: OfficeWorkerIcon,
          onClick: handleMyApplyClick,
        },
        {
          label: 'Account settings',
          Icon: SettingIcon,
          onClick: handleSettingsClick,
        },
      ]
    : [
        {
          label: 'My Profile',
          Icon: ProfileIcon,
          onClick: handleMyProfileClick,
        },
        {
          label: 'My Apply',
          Icon: OfficeWorkerIcon,
          onClick: handleMyApplyClick,
        },
        {
          label: 'Activities',
          Icon: BackpackIcon,
          onClick: handleActivitiesClick,
        },
        {
          label: 'Settings',
          Icon: SettingIcon,
          onClick: handleSettingsClick,
        },
      ];

  // 계정 전환 링크: 비즈니스 계정이면 For Business 숨김, 크리에이터 롤이 있으면 Become a Creator 숨김
  const accountLinks = [
    !isBusiness && { label: 'For Business', onClick: handleForBusinessClick },
    !isCreator && {
      label: 'Become a Creator',
      onClick: handleBecomeCreatorClick,
    },
  ].filter(Boolean);

  return (
    <div
      data-mode={modeAttr}
      className={cx(
        'inline-flex h-full min-h-0 items-center',
        'tabletV:py-[6px] desktopXXL:py-[10px]',
        'data-[mode=scrolled]:py-0',
      )}
    >
      {loading ? (
        <div className={loginSectionPaddingClass}>
          <div className='h-6 w-9 tabletH:hidden' />
          <div className='hidden h-[30px] w-[70px] tabletH:block tabletV:w-[66px] desktopXXL:h-[38px] desktopXXL:w-[80px]' />
        </div>
      ) : !isLoggedIn ? (
        <>
          <BaseButton
            type='text'
            size='s'
            aria-label='Login'
            onClick={handleLoginClick}
            icon={<img src={iconLogin24} alt='login' loading='lazy' />}
            className='h-full w-9 min-w-0 justify-center border-0 bg-transparent p-0 hover:bg-transparent tabletH:hidden'
          />

          <div
            className={cx(
              'hidden shrink-0 tabletH:inline-flex',
              loginSectionPaddingClass,
            )}
          >
            <BaseButton
              type='primary'
              size='s'
              onClick={handleLoginClick}
              bgclass='h-[30px] w-[70px] px-3 py-[6px] tabletV:h-[38px] tabletV:w-[80px] tabletV:py-[10px]'
              hideIcon
              rounded='pill'
              textclass='desktopXXL:!text-[14px]'
            >
              {t('Login')}
            </BaseButton>
          </div>
        </>
      ) : (
        <div className='relative flex items-center justify-center h-full w-9 tabletH:w-auto tabletH:justify-start tabletH:pr-[8px] tabletV:pr-[12px]'>
          <BaseButton
            ref={profileButtonRef}
            type='text'
            size='s'
            aria-label='User profile'
            onClick={handleProfileClick}
            className={cx(
              'min-w-0 shrink-0 border-0 bg-transparent p-0 hover:bg-transparent overflow-hidden rounded-full [&>span]:h-full',
              // 아이콘 버튼이라 눌림 효과(배경색·축소)는 뺀다
              'active:bg-transparent active:scale-100',
              // tabletH 미만은 장바구니 아이콘(24px)과 같은 크기
              'w-6 h-6 tabletH:w-[34px] tabletH:h-[34px] tabletV:w-[30px] tabletV:h-[30px] desktopL:h-10 desktopL:w-10',
            )}
            icon={
              profileImage ? (
                <img
                  src={profileImage}
                  alt='User profile'
                  className='object-cover w-full h-full rounded-full desktopL:h-[30px] desktopL:w-[30px]'
                  loading='lazy'
                />
              ) : (
                <img
                  src={iconHuman28}
                  alt='User profile'
                  className='h-6 w-6 tabletH:h-[26px] tabletH:w-[26px] tabletV:h-auto tabletV:w-auto'
                  loading='lazy'
                />
              )
            }
          />

          {isDropdownOpen && (
            <div
              ref={dropdownRef}
              role='menu'
              aria-label='Profile menu'
              className='absolute right-0 top-[calc(100%+8px)] z-50 w-max min-w-[152px] rounded-[15px] border-[1.6px] border-grayscale-gray02 bg-grayscale-gray01 p-4 shadow-3'
            >
              <div className='flex w-full flex-col items-start gap-2'>
                {profileMenuItems.map(({ label, Icon, onClick }) => {
                  const text = t(label);
                  return (
                    <button
                      key={label}
                      type='button'
                      role='menuitem'
                      onClick={onClick}
                      className={cx(
                        DROPDOWN_ITEM_CLASS,
                        'flex w-full items-center gap-2 text-text-primary',
                        applyTypography('b14Strong', text),
                      )}
                    >
                      <Icon aria-hidden className='size-4 shrink-0' />
                      <span className='whitespace-nowrap'>{text}</span>
                    </button>
                  );
                })}

                <div className='w-full h-px bg-grayscale-gray02' />

                {accountLinks.length > 0 && (
                  <>
                    <div className='flex flex-col items-start'>
                      {accountLinks.map(({ label, onClick }) => {
                        const text = t(label);
                        return (
                          <button
                            key={label}
                            type='button'
                            role='menuitem'
                            onClick={onClick}
                            className={cx(
                              DROPDOWN_ITEM_CLASS,
                              'whitespace-nowrap text-text-placeholder',
                              applyTypography('b13', text),
                            )}
                          >
                            {text}
                          </button>
                        );
                      })}
                    </div>
                    <div className='w-full h-px bg-grayscale-gray02' />
                  </>
                )}

                <button
                  type='button'
                  role='menuitem'
                  onClick={handleLogoutClick}
                  className={cx(
                    DROPDOWN_ITEM_CLASS,
                    'whitespace-nowrap text-text-placeholder',
                    applyTypography('b14', t('Log out')),
                  )}
                >
                  {t('Log out')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isBusiness && (
      <BaseButton
        type='text'
        size='s'
        aria-label='Shopping bag'
        onClick={() => navigate('/community/kpathport/booking')}
        className={cx(
          'h-auto min-w-0 border-0 bg-transparent p-0 hover:bg-transparent hover:opacity-80 transition-opacity',
          // 아이콘 버튼이라 눌림 효과(배경색·축소)는 뺀다
          'active:bg-transparent active:scale-100',
          // mobile 은 36px 영역 가운데, mobile mini 는 왼쪽 6px 만 띄운다
          'h-full w-[30px] justify-center pl-[6px] mobile:w-9 mobile:pl-0',
          'tabletH:mr-[6px] tabletH:h-8 tabletH:w-8 tabletH:p-0 tabletV:mr-[8px] tabletV:h-9 tabletV:w-9',
          'desktopL:mr-[12px]',
        )}
        data-mode={modeAttr}
        icon={
          <span className='relative inline-flex w-6 h-6 tabletH:h-8 tabletH:w-8'>
            <img
              src={iconBag32}
              alt=''
              loading='lazy'
              className='w-full h-full'
            />
            {hasCartItems && (
              <span
                aria-hidden='true'
                className='absolute right-[10%] top-[10%] w-[20%] aspect-square rounded-full bg-[#FF4550]'
              />
            )}
          </span>
        }
      />
      )}

      <LangSelector mode={mode} />
    </div>
  );
}

export default RightControls;
